import React from "react";
import { ArrowRight, Check, CircleDollarSign, ShieldCheck, Star, UsersRound } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const features = [
  ["Gestion des cercles", "Créez ou rejoignez des cercles avec des rôles et des règles clairs.", UsersRound],
  ["Suivi des cotisations", "Voyez chaque paiement, cycle et versement au même endroit.", CircleDollarSign],
  ["Sécurité maximale", "Paiements protégés et sécurité de compte renforcée.", ShieldCheck],
  ["Analyses avancées", "Comprenez votre progression avec des rapports simples et utiles.", Star],
  ["Notifications intelligentes", "Ne manquez aucune cotisation ou versement important.", Check],
  ["Communication", "Gardez votre communauté connectée entre les cycles.", UsersRound],
];

const Home = () => {
  const { t } = useTranslation();
  return <main>
    <section className="overflow-hidden bg-forest text-white">
      <div className="page-shell grid gap-10 py-12 md:grid-cols-[1.08fr_0.92fr] md:items-center md:py-20">
        <div>
          <span className="inline-flex rounded-full border border-gold-300/30 bg-gold-400/10 px-4 py-2 text-xs font-semibold tracking-[0.18em] text-gold-200">
            {t("home.eyebrow")}
          </span>
          <h1 className="mt-6 max-w-2xl font-serif text-5xl font-bold leading-[1.04] sm:text-6xl">
            {t("home.title")}
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-8 text-green-100">
            {t("home.intro")}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/join" className="inline-flex items-center justify-center gap-2 rounded-xl bg-gold-400 px-6 py-3 text-sm font-bold text-forest-deep shadow-lg transition hover:-translate-y-0.5 hover:bg-gold-300">
              {t("home.start")} <ArrowRight size={17} />
            </Link>
            <Link to="/circles" className="inline-flex items-center justify-center rounded-xl border border-white/30 px-6 py-3 text-sm font-bold text-white hover:bg-white/10">
              {t("home.explore")}
            </Link>
          </div>
        </div>

        <div className="relative rounded-3xl border border-white/15 bg-white/10 p-4 shadow-2xl backdrop-blur-sm sm:p-6">
          <div className="rounded-2xl bg-white p-5 text-ink sm:p-7">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold-600">{t("home.howItWorks")}</p>
              <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                {t("home.transparent")}
              </span>
            </div>

            <div className="mt-6 space-y-5">
              {[
                ["01", t("home.steps.choose"), t("home.steps.chooseText")],
                ["02", t("home.steps.contribute"), t("home.steps.contributeText")],
                ["03", t("home.steps.payout"), t("home.steps.payoutText")],
              ].map(([number, title, text]) => (
                <div className="flex gap-4" key={number}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-50 text-xs font-bold text-gold-700">{number}</span>
                  <div>
                    <p className="font-semibold text-forest">{title}</p>
                    <p className="mt-1 text-sm leading-6 text-gray-500">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-7 rounded-xl bg-forest px-4 py-3 text-center text-sm font-semibold text-white">
              {t("home.steps.tagline")}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="border-b border-gold-100 bg-white">
      <div className="page-shell py-12">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end"><div><p className="eyebrow mb-2">{t("home.popularEyebrow")}</p><h2 className="font-serif text-3xl font-bold text-ink">{t("home.popularTitle")}</h2><p className="mt-2 text-sm text-gray-500">{t("home.popularIntro")}</p></div><Link to="/circles" className="btn-secondary">{t("home.viewAll")} <ArrowRight size={16} /></Link></div>
        <div className="mt-8 rounded-2xl border border-gold-200 bg-gold-50 p-8 text-center"><p className="text-sm text-gray-600">{t("home.noCirclesYet")}</p><Link to="/join" className="btn-primary mt-5 inline-flex">{t("home.createFirstCircle")}</Link></div>
      </div>
    </section>

    <section className="bg-forest-deep text-white">
      <div className="page-shell py-16"><p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-gold-400">{t("home.featuresEyebrow")}</p><h2 className="mt-3 text-center font-serif text-4xl font-bold">{t("home.featuresTitle")}</h2><p className="mx-auto mt-3 max-w-xl text-center text-green-100">{t("home.featuresIntro")}</p><div className="mt-10 grid gap-4 md:grid-cols-3">{features.map(([title, text, Icon]) => <div className="rounded-2xl border border-white/10 bg-white/5 p-5" key={title}><Icon className="text-gold-400" size={22} /><h3 className="mt-5 font-semibold">{title}</h3><p className="mt-2 text-sm leading-6 text-green-100">{text}</p></div>)}</div></div>
    </section>

    <section className="bg-forest text-white">
      <div className="page-shell py-14 text-center"><h2 className="font-serif text-4xl font-bold">{t("home.ctaTitle")}</h2><p className="mx-auto mt-3 max-w-xl text-red-50">{t("home.ctaIntro")}</p><Link to="/join" className="mt-7 inline-flex items-center gap-2 rounded-xl bg-gold-400 px-6 py-3 text-sm font-bold text-forest-deep">{t("home.ctaButton")} <ArrowRight size={16} /></Link></div>
    </section>
  </main>;
};

export default Home;
